import { Copy } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, Checkbox, Field, Input, useToast } from "../ds";
import { SIZE_PRESETS, canvasFor, duplicatePageAs, presetOf, resizePage, sizeLabel, variantsOf } from "./pageSize";
import { isBarWidget, type SDef, type SPage } from "./model";

/** Page size for the selected page: presets (TV, A4, Square) or custom, with a preview of how the layout lands
 *  (red = would hang below the page), plus "Duplicate page as A4 / as TV" which keeps both versions linked. */
export function PageSizeControl({ def, page, onChange, onSelectPage }: { def: SDef; page: SPage; onChange: (next: SDef, key?: string) => void; onSelectPage?: (id: string) => void }) {
  const toast = useToast();
  const cur = page.canvas ?? canvasFor(1848, 944);
  const [w, setW] = useState(cur.width);
  const [h, setH] = useState(cur.height);
  const [rescale, setRescale] = useState(true);
  // Keep the inputs in step when another page is selected.
  const [forPage, setForPage] = useState(page.id + "|" + cur.width + "x" + cur.height);
  const key = page.id + "|" + cur.width + "x" + cur.height;
  if (key !== forPage) {
    setForPage(key);
    setW(cur.width);
    setH(cur.height);
  }
  const changed = w !== cur.width || h !== cur.height;
  const preview = useMemo(() => resizePage(page, w, h, rescale), [page, w, h, rescale]);
  const pc = preview.page.canvas ?? cur;
  const red = new Set(preview.overflow);
  const boxes = preview.page.widgets.filter((x) => !isBarWidget(x));
  const rows = Math.floor((pc.height + pc.gap) / (pc.rowHeight + pc.gap));
  const maxW = 150;
  const k = Math.min(maxW / pc.width, 110 / pc.height);
  const colW = (pc.width - pc.gap * (pc.columns - 1)) / pc.columns;
  const px = (l: { x: number; y: number; w: number; h: number }) => ({
    x: l.x * (colW + pc.gap) * k,
    y: l.y * (pc.rowHeight + pc.gap) * k,
    w: (l.w * colW + (l.w - 1) * pc.gap) * k,
    h: (l.h * pc.rowHeight + (l.h - 1) * pc.gap) * k,
  });
  const variants = variantsOf(def, page);

  const apply = () => {
    const r = resizePage(page, w, h, rescale);
    onChange({ ...def, pages: def.pages.map((p) => (p.id === page.id ? r.page : p)) });
    toast.success(`Page is now ${sizeLabel(r.page.canvas)}`, r.overflow.length ? `${r.overflow.length} visual${r.overflow.length === 1 ? "" : "s"} hang below the page - drag them up or resize. Ctrl+Z undoes it.` : "Layout rescaled to fit. Ctrl+Z undoes it.");
  };
  const duplicate = (width: number, height: number, label: string) => {
    const r = duplicatePageAs(def, page.id, width, height, label);
    if (!r) return;
    onChange(r.def);
    onSelectPage?.(r.newId);
    toast.success(`Added "${label}" version of this page`, `Both pages are linked, so viewers can switch between them.${r.overflow.length ? ` ${r.overflow.length} visual(s) need tidying.` : ""}`);
  };

  return (
    <>
      <Field label="Page size" hint="TV pages fill the screens. A4 is for printing and PDF. Each page can have its own size.">
        <div className="st-sizes" role="radiogroup" aria-label="Page size">
          {SIZE_PRESETS.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={presetOf({ width: w, height: h })?.id === p.id} className={`st-size${presetOf({ width: w, height: h })?.id === p.id ? " is-on" : ""}`} title={p.hint} onClick={() => { setW(p.width); setH(p.height); }}>
              <span className="st-size__shape" style={{ aspectRatio: `${p.width} / ${p.height}` }} />
              <span>{p.label}</span>
            </button>
          ))}
        </div>
      </Field>
      <div className="ds-grid ds-grid--2">
        <Field label="Width (px)"><Input inputSize="sm" type="number" min={320} max={4000} value={w} onChange={(e) => setW(Number(e.target.value) || cur.width)} aria-label="Page width" /></Field>
        <Field label="Height (px)"><Input inputSize="sm" type="number" min={240} max={4000} value={h} onChange={(e) => setH(Number(e.target.value) || cur.height)} aria-label="Page height" /></Field>
      </div>
      <Checkbox label="Rescale the layout to the new size" checked={rescale} onChange={setRescale} />
      {changed ? (
        <div className="st-sizeprev">
          <svg width={pc.width * k} height={pc.height * k} role="img" aria-label="Preview of the layout at the new size">
            <rect x={0} y={0} width={pc.width * k} height={pc.height * k} fill="var(--ds-surface-2, #f1f5f9)" stroke="var(--ds-border, #cbd5e1)" />
            {boxes.map((b) => {
              const r = px(b.layout);
              return <rect key={b.id} x={r.x} y={r.y} width={r.w} height={r.h} rx={2} fill={red.has(b.id) ? "rgba(220,38,38,.35)" : "rgba(37,99,235,.3)"} stroke={red.has(b.id) ? "#dc2626" : "#2563eb"} />;
            })}
          </svg>
          <div className="ds-small ds-muted">{w}x{h} - {rows} rows. {preview.overflow.length ? <span className="st-warn">{preview.overflow.length} visual{preview.overflow.length === 1 ? "" : "s"} would hang below the page (red).</span> : "Everything fits."}</div>
          <div className="ds-row" style={{ gap: 8 }}>
            <Button size="sm" variant="primary" onClick={apply}>Apply size</Button>
            <Button size="sm" variant="ghost" onClick={() => { setW(cur.width); setH(cur.height); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="ds-small ds-faint">Now: {sizeLabel(cur)}.</div>
      )}
      <div className="st-section__label" style={{ marginTop: 10 }}>Other sizes of this page</div>
      <div className="ds-row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button size="sm" icon={Copy} title="Copy this page at A4 landscape size, linked to this one" onClick={() => duplicate(1123, 794, "A4")}>Duplicate as A4</Button>
        <Button size="sm" icon={Copy} title="Copy this page at TV size, linked to this one" onClick={() => duplicate(1848, 944, "TV")}>Duplicate as TV</Button>
      </div>
      {variants.length > 1 ? (
        <div className="ds-small ds-muted" style={{ marginTop: 6 }}>
          Linked versions: {variants.map((v, i) => (
            <span key={v.id}>{i ? ", " : ""}{v.id === page.id ? <strong>{v.name}</strong> : <button type="button" className="st-link" onClick={() => onSelectPage?.(v.id)}>{v.name}</button>}</span>
          ))}. Viewers get a switch between them.
        </div>
      ) : null}
    </>
  );
}
